import { useQuery, useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { projectService } from '../services/projectService';
import { ProjectList } from './ProjectList';

export const ProjectManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const {
    data: projectsResponse,
    isLoading: isLoadingProjects,
    error: projectsError,
    refetch: refetchProjects,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectService.getProjects(),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const projects = projectsResponse || [];

  if (isLoadingProjects) {
    return (
      <div className="flex min-h-[400px] items-center justify-center rounded-lg bg-surface-card p-8 shadow-sm">
        <div className="flex flex-col items-center gap-4 text-text-secondary">
          <Spinner className="size-10" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (projectsError) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 rounded-lg bg-surface-card p-8 text-danger-600 shadow-sm">
        <p>
          Error loading data:{' '}
          {projectsError instanceof Error ? projectsError.message : 'Unknown error'}
        </p>
        <Button type="button" onClick={() => void refetchProjects()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="p-2">
      <ProjectList
        projects={projects}
        isLoading={isLoadingProjects}
        onEdit={() => {
          queryClient.invalidateQueries({ queryKey: ['projects'] });
        }}
        onArchive={async (projectId) => {
          if (
            window.confirm(
              'Are you sure you want to archive this project? It will be hidden from the active projects list.'
            )
          ) {
            try {
              await projectService.archiveProject(projectId);
              await refetchProjects();
            } catch (error) {
              alert(
                `Failed to archive project: ${error instanceof Error ? error.message : 'Unknown error'}`
              );
            }
          }
        }}
        onRestore={async (projectId) => {
          try {
            await projectService.restoreProject(projectId);
            await refetchProjects();
          } catch (error) {
            alert(
              `Failed to restore project: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
          }
        }}
        onDeleteProject={async (projectId) => {
          try {
            await projectService.deleteProject(projectId);
            await refetchProjects();
          } catch (error) {
            alert(
              `Failed to delete project: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
          }
        }}
        onAdd={() => {
          queryClient.invalidateQueries({ queryKey: ['projects'] });
        }}
      />
    </div>
  );
};
